import { useState, useEffect, useRef, useCallback } from "react";
import { logger } from "@/utils/logger";
import { motion, AnimatePresence } from "framer-motion";
import { Home, RotateCcw, Volume2, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";
import Confetti from "react-confetti";
import { useWindowSize } from "@/hooks/useWindowSize";
import { savePlayerNames, loadPlayerNames } from "@/utils/playerStorage";
import { recordGameWon } from "@/utils/playerProfile";

import { getVoiceLang } from "@/utils/voicePref";
const BLOCK_COLORS = ["#FF6B6B", "#4ECDC4", "#FFD93D", "#6C5CE7", "#FF8C00", "#E91E63", "#00BCD4", "#8BC34A", "#FF5722", "#9C27B0"];

const speakText = (t) => {
  try { if (!window.speechSynthesis) return; window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(t); u.lang = getVoiceLang(); u.rate=0.85; u.pitch=1.0; window.speechSynthesis.speak(u);
  } catch (err) { logger.debug("game op failed", err); }
};

const playPlaceSound = () => {
  try { const c = new (window.AudioContext || window.webkitAudioContext)();
    const o = c.createOscillator(); const g = c.createGain();
    o.connect(g); g.connect(c.destination); o.frequency.value=400; o.type="sine";
    g.gain.setValueAtTime(0.2,c.currentTime); g.gain.exponentialRampToValueAtTime(0.001,c.currentTime+0.1);
    o.start(c.currentTime); o.stop(c.currentTime+0.1);
  } catch (err) { logger.debug("game op failed", err); }
};

const MenaraBalok = ({ onBackToMenu }) => {
  const { width: winW, height: winH } = useWindowSize();
  const [gameState, setGameState] = useState("menu");
  const [playerNames, setPlayerNames] = useState(() => { const s = loadPlayerNames(); return [s[0]||"",s[1]||""]; });
  const [currentPlayer, setCurrentPlayer] = useState(0);
  const [blocks, setBlocks] = useState([]);
  const [swingX, setSwingX] = useState(50);
  const [swingDir, setSwingDir] = useState(1);
  const [gameOver, setGameOver] = useState(false);
  const [soundOn, setSoundOn] = useState(true);
  const [speed, setSpeed] = useState(1.5);
  const rafRef = useRef(null);

  const p1 = playerNames[0] || "Pemain 1";
  const p2 = playerNames[1] || "Pemain 2";
  const towerH = blocks.length;
  const maxH = 15;

  const startGame = () => {
    savePlayerNames(playerNames);
    setBlocks([]); setCurrentPlayer(0); setGameOver(false); setSwingX(50); setSpeed(1.5);
    setGameState("playing");
  };

  // Swing animation
  useEffect(() => {
    if (gameState !== "playing" || gameOver) return;
    const animate = () => {
      setSwingX(prev => {
        let next = prev + swingDir * speed;
        if (next >= 90 || next <= 10) setSwingDir(d => -d);
        return Math.max(10, Math.min(90, next));
      });
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [gameState, gameOver, swingDir, speed]);

  const dropBlock = () => {
    if (gameOver || gameState !== "playing") return;
    const blockWidth = Math.max(12, 30 - blocks.length * 1.2);
    const blockX = swingX;

    // Check alignment with previous block
    if (blocks.length > 0) {
      const lastBlock = blocks[blocks.length - 1];
      const overlap = Math.abs(blockX - lastBlock.x);
      if (overlap > blockWidth * 0.8) {
        setGameOver(true);
        recordGameWon("menara");
        if (soundOn) speakText(`Tower fell! ${currentPlayer === 0 ? p2 : p1} wins with ${towerH} blocks!`);
        return;
      }
    }

    if (soundOn) playPlaceSound();
    const color = BLOCK_COLORS[blocks.length % BLOCK_COLORS.length];
    setBlocks(prev => [...prev, { x: blockX, w: blockWidth, color, player: currentPlayer }]);
    setCurrentPlayer(prev => prev === 0 ? 1 : 0);
    setSpeed(prev => Math.min(prev + 0.15, 4));

    if (blocks.length + 1 >= maxH) {
      setGameOver(true);
      if (soundOn) speakText(`Amazing! Tower ${maxH} blocks completed!`);
    }
  };

  if (gameState === "menu") {
    return (
      <div className="flex flex-col items-center w-full max-w-2xl mx-auto px-4 pt-4" data-testid="menara-balok-menu">
        <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="glass-card-strong rounded-2xl p-6 w-full text-center mb-4">
          <motion.span className="text-6xl block mb-3" animate={{ y: [0,-8,0] }} transition={{ duration: 1.5, repeat: Infinity }}>🏗️</motion.span>
          <h1 className="font-fredoka text-3xl text-gold font-bold mb-2">Menara Balok</h1>
          <p className="font-fredoka text-gold-light/80 text-sm mb-4">Susun balok setinggi mungkin tanpa jatuh!</p>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <input data-testid="menara-p1" type="text" value={playerNames[0]} onChange={e => setPlayerNames([e.target.value, playerNames[1]])} placeholder="Pemain 1" maxLength={15} className="glass-card border-gold/20 rounded-xl px-3 py-2 text-white font-fredoka text-sm focus:outline-none" />
            <input data-testid="menara-p2" type="text" value={playerNames[1]} onChange={e => setPlayerNames([playerNames[0], e.target.value])} placeholder="Pemain 2" maxLength={15} className="glass-card border-gold/20 rounded-xl px-3 py-2 text-white font-fredoka text-sm focus:outline-none" />
          </div>
          <Button onClick={startGame} className="w-full h-12 font-fredoka font-bold text-lg rounded-xl bg-gradient-to-r from-orange-500 to-red-500" data-testid="menara-start">MULAI SUSUN!</Button>
        </motion.div>
        <Button onClick={onBackToMenu} variant="outline" className="w-full glass-card border-gold/20 text-gold hover:bg-gold/10 font-fredoka rounded-xl"><Home className="w-4 h-4 mr-2" />Menu</Button>
      </div>
    );
  }

  const containerH = Math.min(winH - 180, 450);

  return (
    <div className="flex flex-col items-center w-full max-w-2xl mx-auto px-2" data-testid="menara-balok-game">
      {gameOver && towerH >= maxH && <Confetti width={winW} height={winH} recycle={false} numberOfPieces={300} />}
      <div className="w-full glass-card-strong rounded-xl p-2 mb-2 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <span className="font-fredoka text-gold font-bold text-sm">🏗️ Tinggi: {towerH}</span>
          <span className={`font-fredoka text-xs ${currentPlayer === 0 ? 'text-cyan-400' : 'text-pink-400'} font-bold`}>Giliran {currentPlayer === 0 ? p1 : p2}</span>
        </div>
        <div className="flex gap-1.5">
          <Button onClick={() => setSoundOn(!soundOn)} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 w-7 p-0">{soundOn ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}</Button>
          <Button onClick={() => setGameState("menu")} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 px-2"><Home className="w-3 h-3" /></Button>
        </div>
      </div>

      {/* Tower area */}
      <div className="w-full rounded-2xl overflow-hidden relative border-2 border-orange-400/30"
        style={{ height: containerH, background: "linear-gradient(180deg, #1a1a2e 0%, #16213e 60%, #0f3460 100%)" }}>
        {/* Stars */}
        {[...Array(8)].map((_, i) => (
          <motion.div key={i} className="absolute text-xs select-none" style={{ left: `${10+i*11}%`, top: `${5+i*7}%` }}
            animate={{ opacity: [0.3, 0.8, 0.3] }} transition={{ duration: 2, repeat: Infinity, delay: i*0.3 }}>⭐</motion.div>
        ))}

        {/* Swing indicator (pending block) */}
        {!gameOver && (
          <motion.div className="absolute top-4 z-20 rounded-lg"
            style={{
              left: `${swingX}%`, transform: 'translateX(-50%)',
              width: `${Math.max(12, 30 - blocks.length * 1.2)}%`, height: 24,
              background: BLOCK_COLORS[blocks.length % BLOCK_COLORS.length],
              boxShadow: `0 4px 12px ${BLOCK_COLORS[blocks.length % BLOCK_COLORS.length]}60`,
            }} />
        )}
        {/* Drop line */}
        {!gameOver && (
          <div className="absolute top-8 w-0.5 opacity-20 bg-white" style={{ left: `${swingX}%`, height: containerH - 40 }} />
        )}

        {/* Stacked blocks */}
        {blocks.map((block, i) => (
          <motion.div key={i} className="absolute rounded-md"
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            style={{
              left: `${block.x}%`, transform: 'translateX(-50%)',
              bottom: `${i * (100 / maxH)}%`,
              width: `${block.w}%`, height: `${Math.max(4, 90 / maxH)}%`,
              background: `linear-gradient(180deg, ${block.color}, ${block.color}CC)`,
              boxShadow: `0 2px 6px ${block.color}40`,
              border: `1px solid ${block.color}80`,
            }}>
            <div className="absolute inset-0 rounded-md opacity-30" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.3), transparent)' }} />
          </motion.div>
        ))}

        {/* Ground */}
        <div className="absolute bottom-0 left-0 right-0 h-[3%] bg-gradient-to-t from-amber-800 to-amber-700" />

        {/* Game over overlay */}
        {gameOver && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="absolute inset-0 flex items-center justify-center z-30 bg-black/50">
            <div className="glass-card-strong rounded-2xl p-6 text-center max-w-sm">
              <span className="text-5xl block mb-2">{towerH >= maxH ? "🎉" : "💥"}</span>
              <h2 className="font-fredoka text-2xl text-gold font-bold mb-1">{towerH >= maxH ? "Luar Biasa!" : "Menara Jatuh!"}</h2>
              <p className="font-fredoka text-gold-light/80 text-sm mb-3">Tinggi: {towerH} balok</p>
              {towerH < maxH && <p className="font-fredoka text-sm text-gold-light/60 mb-3">{currentPlayer === 0 ? p2 : p1} menang!</p>}
              <div className="flex flex-col gap-2">
                <Button onClick={startGame} className="w-full font-fredoka font-bold rounded-xl bg-gradient-to-r from-yellow-400 to-amber-500"><RotateCcw className="w-4 h-4 mr-2" />Main Lagi</Button>
                <Button onClick={() => setGameState("menu")} variant="outline" className="w-full glass-card border-gold/20 text-gold font-fredoka rounded-xl">Menu</Button>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Drop button */}
      {!gameOver && (
        <Button onClick={dropBlock} data-testid="menara-drop"
          className="w-full h-16 mt-3 text-xl font-fredoka font-bold rounded-2xl bg-gradient-to-r from-orange-400 to-red-500 btn-3d"
          style={{ touchAction: "manipulation" }}>
          🧱 JATUHKAN BALOK!
        </Button>
      )}
    </div>
  );
};

export default MenaraBalok;
